import { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import InviteAccept from './pages/InviteAccept';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Equipment from './pages/Equipment';
import Personnel from './pages/Personnel';
import EquipmentPlanner from './pages/EquipmentPlanner';
import PersonnelPlanner from './pages/PersonnelPlanner';
import Reports from './pages/Reports';
import MOBTracker from './pages/MOBTracker';
import Users from './pages/Users';
import Branding from './pages/Branding';
import DataManagement from './pages/DataManagement';
import ComplianceDashboard from './pages/ComplianceDashboard';
import Projects from './pages/Projects';
import Sidebar from './components/Sidebar';
import TopNav from './components/TopNav';
import { NotificationProvider } from './components/NotificationProvider';
import { PermissionsProvider } from './components/PermissionsProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Session expiry handler component
function SessionHandler({ setUser }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      navigate('/login', { replace: true });
    };
    
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [navigate, setUser]);
  
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup axios interceptor for 401 handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setUser(null);
          window.dispatchEvent(new CustomEvent('session-expired'));
        }
        return Promise.reject(error);
      }
    );
    
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        setUser(response.data);
      })
      .catch(() => {
        localStorage.removeItem('token');
      })
      .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Apply per-tenant app theme (e.g. red/black for tenants with the industrial-hero
  // branding). Driven by the tenant's branding config; defaults to the standard theme.
  useEffect(() => {
    const root = document.documentElement;
    const applyBranding = (branding) => {
      const isIndustrial = branding && branding.theme === 'industrial-hero';
      if (isIndustrial) {
        root.setAttribute('data-tenant-theme', 'industrial');
        const accent = branding.accent || '#DC2626';
        const accentDark = branding.accent_hover || accent;
        root.style.setProperty('--tenant-accent', accent);
        root.style.setProperty('--tenant-accent-dark', accentDark);
        root.style.setProperty('--tenant-accent-soft', accent + '14');
        root.style.setProperty('--tenant-accent-line', accent + '40');
      } else {
        root.removeAttribute('data-tenant-theme');
        ['--tenant-accent', '--tenant-accent-dark', '--tenant-accent-soft', '--tenant-accent-line']
          .forEach((v) => root.style.removeProperty(v));
      }
    };
    if (!user) { applyBranding(null); return; }
    try {
      const cached = JSON.parse(localStorage.getItem('tenant_branding') || 'null');
      if (cached) { applyBranding(cached); return; }
    } catch { /* ignore */ }
    // Fallback for sessions started before branding was cached
    try {
      const tenant = JSON.parse(localStorage.getItem('tenant') || 'null');
      if (tenant?.client_id) {
        axios.get(`${API}/tenants/${encodeURIComponent(tenant.client_id)}/branding`)
          .then((r) => {
            const branding = r.data || { theme: 'classic' };
            localStorage.setItem('tenant_branding', JSON.stringify(branding));
            applyBranding(branding);
          })
          .catch(() => {});
      }
    } catch { /* ignore */ }
  }, [user]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <NotificationProvider>
      <PermissionsProvider>
      <div className="App">
        <BrowserRouter>
          <SessionHandler setUser={setUser} />
          <Routes>
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/invite" element={<InviteAccept />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <div className="flex h-screen bg-slate-50">
                    <Sidebar user={user} setUser={setUser} />
                    <div className="flex-1 flex flex-col overflow-hidden">
                      <TopNav user={user} setUser={setUser} />
                      <div className="flex-1 overflow-y-auto">
                        <Routes>
                          <Route path="/" element={<Dashboard user={user} />} />
                          <Route path="/equipment" element={<Equipment user={user} />} />
                          <Route path="/personnel" element={<Personnel user={user} />} />
                          <Route path="/projects" element={<Projects user={user} />} />
                          <Route path="/equipment-planner" element={<EquipmentPlanner user={user} />} />
                          <Route path="/personnel-planner" element={<PersonnelPlanner user={user} />} />
                          <Route path="/reports" element={<Reports user={user} />} />
                          <Route path="/compliance" element={<ComplianceDashboard user={user} />} />
                          <Route path="/mob-tracker" element={<MOBTracker user={user} />} />
                          <Route path="/users" element={<Users user={user} />} />
                          <Route path="/branding" element={<Branding user={user} />} />
                          <Route path="/data-management" element={<DataManagement user={user} />} />
                        </Routes>
                      </div>
                    </div>
                  </div>
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
      </PermissionsProvider>
    </NotificationProvider>
  );
}

export default App;